<template>
	<div class="shared-download">
		<Card class="shared-download__card">
			<template #header>
				<div class="shared-download__logo-container">
					<img
						alt="codecannon logo"
						class="shared-download__logo"
						src="@/assets/logo.svg" />
				</div>
			</template>
			<template #title>
				<div
					v-if="token && auth.user?.verified && details"
					class="shared-download__title">
					{{ details.app_name }}
					<Tag
						v-if="details.build_version !== null"
						severity="info"
						:value="`v${details.build_version}`" />
				</div>
				<template v-else>{{ title }}</template>
			</template>
			<template #content>
				<div class="shared-download__content">
					<p v-if="!token">Open the download link from your email again.</p>
					<template v-else-if="!auth.user">
						<p>Sign in to download. Any verified CodeCannon account works.</p>
						<div class="shared-download__actions">
							<Button
								label="Sign in"
								@click="router.push({ name: 'login' })" />
							<Button
								label="Create account"
								outlined
								@click="router.push({ name: 'register' })" />
						</div>
					</template>
					<template v-else-if="!auth.user.verified">
						<p>Verify your email address to download.</p>
						<Button
							label="Verify email"
							@click="router.push({ name: 'verify-email-notice' })" />
					</template>
					<template v-else-if="details">
						<p class="shared-download__muted">
							Source code ZIP · expires {{ format(new Date(details.expires_at), 'PPP') }}
						</p>
						<Message
							v-if="started"
							role="status"
							severity="success"
							size="small">
							Download started.<template v-if="details.retry_until">
								You can download again until {{ formatTime(details.retry_until) }}.</template
							>
						</Message>
						<Message
							v-if="error"
							severity="error"
							size="small">
							{{ error }}
						</Message>
						<Button
							icon="fal fa-download"
							:label="started || details.retry_until ? 'Download again' : 'Download ZIP'"
							:loading="downloading"
							@click="download" />
						<p
							v-if="!started && details.retry_until"
							class="shared-download__muted">
							You can download again until {{ formatTime(details.retry_until) }}.
						</p>
						<p
							v-else-if="!started"
							class="shared-download__muted">
							Downloading claims this link for your account. Retries end after one hour or when the
							link expires.
						</p>
					</template>
					<template v-else-if="error">
						<Message
							severity="error"
							size="small">
							{{ error }}
						</Message>
						<p
							v-if="finalError"
							class="shared-download__muted">
							Ask the sender for a new link.
						</p>
						<Button
							v-else
							label="Try again"
							outlined
							@click="load" />
					</template>
					<p
						v-else
						class="shared-download__muted">
						Checking your link…
					</p>
				</div>
			</template>
		</Card>
	</div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { onBeforeRouteUpdate, useRouter } from 'vue-router'
import axios from 'axios'
import { format } from 'date-fns'
import Card from 'primevue/card'
import Button from 'primevue/button'
import Message from 'primevue/message'
import Tag from 'primevue/tag'
import { useAuthStore } from '@/stores/Auth'
import emailDownloadGuard from '@/router/guards/emailDownloadGuard'
import type { SharedDownload } from '@/models/EmailDownload/Model'
import EmailDownloadsApi from '@/models/EmailDownload/Api'
import { emailDownloadError } from '@/models/EmailDownload/Errors'
import { captureEmailDownloadToken, emailDownloadToken } from '@/models/EmailDownload/Link'

captureEmailDownloadToken()
const token = ref(emailDownloadToken())
const auth = useAuthStore()
const router = useRouter()
const api = new EmailDownloadsApi()
const details = ref<SharedDownload | null>(null)
const error = ref('')
const errorStatus = ref<number | null>(null)
const loading = ref(false)
const downloading = ref(false)
const started = ref(false)
// Bumped by every load and download, so results for a replaced link are ignored.
let operation = 0

// Revoked, expired or claimed by another account: retrying can't help.
const finalError = computed(() => errorStatus.value === 410 || errorStatus.value === 403)
const title = computed(() => {
	if (!token.value) return 'Link missing'
	if (auth.user && !auth.user.verified) return 'Verify your email'
	if (auth.user && error.value && !loading.value) return 'Link unavailable'
	return 'Download source code'
})

onMounted(load)
onBeforeRouteUpdate((to) => {
	const redirect = emailDownloadGuard(to)
	token.value = emailDownloadToken()
	details.value = null
	started.value = false
	downloading.value = false
	void load()
	return redirect
})

function formatTime(date: string) {
	return format(new Date(date), 'p')
}

async function fail(exception: unknown, current: number) {
	const message = await emailDownloadError(exception)
	if (current !== operation) return
	const status = axios.isAxiosError(exception) ? (exception.response?.status ?? null) : null
	if (status === 401) auth.user = null
	error.value = message
	errorStatus.value = status
	// The link can't be downloaded from this page anymore.
	if (finalError.value) details.value = null
}

async function load() {
	const requestedToken = token.value
	if (!requestedToken) return
	const current = ++operation
	loading.value = true
	error.value = ''
	errorStatus.value = null
	try {
		await auth.getUser()
		if (auth.user?.verified) {
			const response = await api.preview(requestedToken)
			if (current === operation) details.value = response.data
		}
	} catch (exception) {
		await fail(exception, current)
	} finally {
		if (current === operation) loading.value = false
	}
}

async function download() {
	if (!token.value || !details.value || downloading.value) return
	const current = ++operation
	const requestedToken = token.value
	const requestedDetails = details.value
	downloading.value = true
	error.value = ''
	errorStatus.value = null
	try {
		const response = await api.download(requestedToken)
		const url = URL.createObjectURL(response.data)
		const link = document.createElement('a')
		link.href = url
		link.download = `${requestedDetails.app_name.replace(/[^a-zA-Z0-9_-]/g, '_')}-v${requestedDetails.build_version ?? 'build'}.zip`
		document.body.appendChild(link)
		link.click()
		link.remove()
		setTimeout(() => URL.revokeObjectURL(url), 60000)
		if (current === operation) {
			started.value = true
			const response = await api.preview(requestedToken)
			if (current === operation) details.value = response.data
		}
	} catch (exception) {
		await fail(exception, current)
	} finally {
		if (current === operation) downloading.value = false
	}
}
</script>

<style scoped lang="scss">
.shared-download {
	display: flex;
	align-items: center;
	justify-content: center;
	min-height: 100vh;
	padding: 16px;
	width: 100%;

	.shared-download__card {
		width: 500px;
		max-width: 100%;
		overflow: hidden;

		.shared-download__logo {
			padding-left: 45px;
			padding-right: 45px;
			padding-bottom: 20px;
			padding-top: 20px;
			background: #eeff01;
			margin: auto;
			display: block;
			max-width: 100%;
		}

		.shared-download__title {
			display: flex;
			align-items: center;
			flex-wrap: wrap;
			gap: 8px;
		}

		.shared-download__content {
			display: flex;
			flex-direction: column;
			gap: 16px;

			p {
				margin: 0;
			}

			.shared-download__actions {
				display: flex;
				gap: 8px;

				& > * {
					flex: 1;
				}

				@media (max-width: 480px) {
					flex-direction: column;
				}
			}

			.shared-download__muted {
				color: var(--p-text-muted-color);
				font-size: 13px;
			}
		}
	}
}
</style>
